import React, { useState } from 'react';
import {
    Space,
    Button,
    Alert,
    Spin,
    Modal,
    message,
    Form,
    Input,
    Switch,
    Table,
    Tag,
    Popconfirm,
    InputNumber,
    Select,
    Typography,
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAuth } from '../../hooks/useAuth';
import {
    useGetAdminQuestionTemplateTypesQuery,
    useCreateQuestionTemplateTypeMutation,
    useUpdateQuestionTemplateTypeMutation,
    useDeleteQuestionTemplateTypeMutation,
    useGetAdminBenchmarkBusinessTypesQuery,
    useGetAdminBenchmarkProductTypesQuery,
    IQuestionTemplateType,
    IQuestionTemplateTypeInput,
} from '../../services/nestjs-base-api';

const { Text } = Typography;

const SLUG_PATTERN = /^[a-z][a-z0-9_]*$/;

/**
 * Admin CRUD for the config-driven template-type registry (SYR-74). Each row
 * maps a template_type slug to its benchmarking product_type and benchmark
 * dimension slugs — the single bridge that lets a new vertical go live without
 * a code change. Lives beside the clone wizard on the Statement Templates page.
 */
export const TemplateTypeRegistryPanel: React.FC = () => {
    const { user } = useAuth();
    const intl = useIntl();
    const jwtToken = user?.jwtToken || '';

    const { data: rows, isLoading } = useGetAdminQuestionTemplateTypesQuery({
        jwtToken,
    });
    const { data: businessTypes } = useGetAdminBenchmarkBusinessTypesQuery({
        jwtToken,
    });
    const { data: productTypes } = useGetAdminBenchmarkProductTypesQuery({
        jwtToken,
    });

    const [createType] = useCreateQuestionTemplateTypeMutation();
    const [updateType] = useUpdateQuestionTemplateTypeMutation();
    const [deleteType] = useDeleteQuestionTemplateTypeMutation();

    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState<IQuestionTemplateType | null>(null);
    const [saving, setSaving] = useState(false);
    const [form] = Form.useForm<IQuestionTemplateTypeInput>();

    const openCreate = () => {
        setEditing(null);
        form.resetFields();
        form.setFieldsValue({ isActive: true, displayOrder: 0 });
        setModalOpen(true);
    };

    const openEdit = (row: IQuestionTemplateType) => {
        setEditing(row);
        form.setFieldsValue({
            slug: row.slug,
            label: row.label,
            productType: row.productType || undefined,
            benchmarkBusinessTypeSlug: row.benchmarkBusinessTypeSlug || undefined,
            benchmarkProductTypeSlug: row.benchmarkProductTypeSlug || undefined,
            displayOrder: row.displayOrder,
            isActive: row.isActive,
        });
        setModalOpen(true);
    };

    const handleSubmit = async () => {
        let values: IQuestionTemplateTypeInput;
        try {
            values = await form.validateFields();
        } catch {
            return;
        }
        setSaving(true);
        try {
            if (editing?.id) {
                await updateType({
                    jwtToken,
                    id: editing.id,
                    data: {
                        label: values.label,
                        productType: values.productType || null,
                        benchmarkBusinessTypeSlug:
                            values.benchmarkBusinessTypeSlug || null,
                        benchmarkProductTypeSlug:
                            values.benchmarkProductTypeSlug || null,
                        displayOrder: values.displayOrder,
                        isActive: values.isActive,
                    },
                }).unwrap();
            } else {
                await createType({ jwtToken, data: values }).unwrap();
            }
            message.success(
                intl.formatMessage({
                    id: 'admin.templateTypes.saveSuccess',
                    defaultMessage: 'Template type saved',
                }),
            );
            setModalOpen(false);
        } catch (error: any) {
            message.error(
                error?.data?.message ||
                    intl.formatMessage({
                        id: 'admin.templateTypes.saveFailed',
                        defaultMessage: 'Failed to save template type',
                    }),
            );
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (row: IQuestionTemplateType) => {
        try {
            await deleteType({ jwtToken, id: row.id as number }).unwrap();
            message.success(
                intl.formatMessage({
                    id: 'admin.templateTypes.deleteSuccess',
                    defaultMessage: 'Template type deleted',
                }),
            );
        } catch (error: any) {
            message.error(
                error?.data?.message ||
                    intl.formatMessage({
                        id: 'admin.templateTypes.deleteFailed',
                        defaultMessage: 'Failed to delete template type',
                    }),
            );
        }
    };

    const columns: ColumnsType<IQuestionTemplateType> = [
        {
            title: intl.formatMessage({
                id: 'admin.templateTypes.slug',
                defaultMessage: 'Slug',
            }),
            dataIndex: 'slug',
            key: 'slug',
            width: 220,
            render: (slug: string, row) => (
                <Space wrap size={4}>
                    <Text code style={{ whiteSpace: 'nowrap' }}>
                        {slug}
                    </Text>
                    {row.isBuiltIn && (
                        <Tag color="blue" style={{ marginInlineEnd: 0 }}>
                            <FormattedMessage
                                id="admin.templateTypes.builtIn"
                                defaultMessage="built-in"
                            />
                        </Tag>
                    )}
                </Space>
            ),
        },
        {
            title: intl.formatMessage({
                id: 'admin.templateTypes.label',
                defaultMessage: 'Label',
            }),
            dataIndex: 'label',
            key: 'label',
        },
        {
            title: intl.formatMessage({
                id: 'admin.templateTypes.productType',
                defaultMessage: 'Product Type',
            }),
            dataIndex: 'productType',
            key: 'productType',
            render: (v: string | null) => (v ? <Text>{v}</Text> : <Text type="secondary">—</Text>),
        },
        {
            title: intl.formatMessage({
                id: 'admin.templateTypes.businessType',
                defaultMessage: 'Benchmark Business Type',
            }),
            dataIndex: 'benchmarkBusinessTypeSlug',
            key: 'benchmarkBusinessTypeSlug',
            render: (v: string | null) => (v ? <Tag>{v}</Tag> : <Text type="secondary">—</Text>),
        },
        {
            title: intl.formatMessage({
                id: 'admin.templateTypes.benchmarkProductType',
                defaultMessage: 'Benchmark Product Type',
            }),
            dataIndex: 'benchmarkProductTypeSlug',
            key: 'benchmarkProductTypeSlug',
            render: (v: string | null) => (v ? <Tag>{v}</Tag> : <Text type="secondary">—</Text>),
        },
        {
            title: intl.formatMessage({
                id: 'admin.templateTypes.active',
                defaultMessage: 'Active',
            }),
            dataIndex: 'isActive',
            key: 'isActive',
            width: 90,
            align: 'center' as const,
            render: (active: boolean) =>
                active ? (
                    <Tag color="green">
                        <FormattedMessage
                            id="admin.templateTypes.statusActive"
                            defaultMessage="active"
                        />
                    </Tag>
                ) : (
                    <Tag color="red">
                        <FormattedMessage
                            id="admin.templateTypes.statusInactive"
                            defaultMessage="inactive"
                        />
                    </Tag>
                ),
        },
        {
            title: intl.formatMessage({
                id: 'admin.templateTypes.actions',
                defaultMessage: 'Actions',
            }),
            key: 'actions',
            width: 120,
            render: (_, row) => (
                <Space>
                    <Button
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => openEdit(row)}
                    />
                    <Popconfirm
                        title={intl.formatMessage({
                            id: 'admin.templateTypes.confirmDelete',
                            defaultMessage: 'Delete this template type mapping?',
                        })}
                        onConfirm={() => handleDelete(row)}
                        disabled={row.isBuiltIn}
                    >
                        <Button
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            disabled={row.isBuiltIn}
                        />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    if (isLoading) {
        return (
            <div style={{ textAlign: 'center', padding: 40 }}>
                <Spin />
            </div>
        );
    }

    return (
        <div>
            <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message={
                    <FormattedMessage
                        id="admin.templateTypes.hint"
                        defaultMessage="Each mapping wires a template type to its benchmarking product type and benchmark dimensions. Add a mapping here (plus authored statements) to light up a new vertical — no deployment needed."
                    />
                }
            />

            <Space style={{ marginBottom: 12 }}>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={openCreate}
                >
                    <FormattedMessage
                        id="admin.templateTypes.add"
                        defaultMessage="Add Mapping"
                    />
                </Button>
            </Space>

            <Table
                rowKey="slug"
                size="small"
                columns={columns}
                dataSource={rows || []}
                pagination={false}
            />

            <Modal
                title={
                    editing ? (
                        <FormattedMessage
                            id="admin.templateTypes.editTitle"
                            defaultMessage="Edit Template Type"
                        />
                    ) : (
                        <FormattedMessage
                            id="admin.templateTypes.addTitle"
                            defaultMessage="Add Template Type"
                        />
                    )
                }
                open={modalOpen}
                onOk={handleSubmit}
                confirmLoading={saving}
                onCancel={() => setModalOpen(false)}
                destroyOnClose
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="slug"
                        label={intl.formatMessage({
                            id: 'admin.templateTypes.slug',
                            defaultMessage: 'Slug',
                        })}
                        rules={[
                            {
                                required: true,
                                message: intl.formatMessage({
                                    id: 'admin.templateTypes.slugRequired',
                                    defaultMessage: 'Slug is required',
                                }),
                            },
                            {
                                pattern: SLUG_PATTERN,
                                message: intl.formatMessage({
                                    id: 'admin.templateTypes.slugPattern',
                                    defaultMessage:
                                        'Lowercase letters, digits and underscores; must start with a letter (e.g. sports_betting)',
                                }),
                            },
                        ]}
                        extra={intl.formatMessage({
                            id: 'admin.templateTypes.slugExtra',
                            defaultMessage:
                                'Must match the template_type of the authored statements.',
                        })}
                    >
                        <Input disabled={!!editing} placeholder="sports_betting" />
                    </Form.Item>

                    <Form.Item
                        name="label"
                        label={intl.formatMessage({
                            id: 'admin.templateTypes.label',
                            defaultMessage: 'Label',
                        })}
                        rules={[{ required: true }]}
                    >
                        <Input placeholder="Sports Betting" />
                    </Form.Item>

                    <Form.Item
                        name="productType"
                        label={intl.formatMessage({
                            id: 'admin.templateTypes.productType',
                            defaultMessage: 'Product Type',
                        })}
                        extra={intl.formatMessage({
                            id: 'admin.templateTypes.productTypeExtra',
                            defaultMessage:
                                'Long-form product_type stored on the brand (e.g. durable_goods, sports_betting).',
                        })}
                    >
                        <Input placeholder="sports_betting" allowClear />
                    </Form.Item>

                    <Form.Item
                        name="benchmarkBusinessTypeSlug"
                        label={intl.formatMessage({
                            id: 'admin.templateTypes.businessType',
                            defaultMessage: 'Benchmark Business Type',
                        })}
                    >
                        <Select
                            allowClear
                            placeholder={intl.formatMessage({
                                id: 'admin.templateTypes.selectBusinessType',
                                defaultMessage: 'Select business type',
                            })}
                            options={(businessTypes || []).map((bt: any) => ({
                                value: bt.slug,
                                label: `${bt.name} (${bt.slug})`,
                            }))}
                        />
                    </Form.Item>

                    <Form.Item
                        name="benchmarkProductTypeSlug"
                        label={intl.formatMessage({
                            id: 'admin.templateTypes.benchmarkProductType',
                            defaultMessage: 'Benchmark Product Type',
                        })}
                    >
                        <Select
                            allowClear
                            placeholder={intl.formatMessage({
                                id: 'admin.templateTypes.selectProductType',
                                defaultMessage: 'Select product type',
                            })}
                            options={(productTypes || []).map((pt: any) => ({
                                value: pt.slug,
                                label: `${pt.name} (${pt.slug})`,
                            }))}
                        />
                    </Form.Item>

                    <Form.Item
                        name="displayOrder"
                        label={intl.formatMessage({
                            id: 'admin.templateTypes.displayOrder',
                            defaultMessage: 'Display Order',
                        })}
                    >
                        <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item
                        name="isActive"
                        label={intl.formatMessage({
                            id: 'admin.templateTypes.active',
                            defaultMessage: 'Active',
                        })}
                        valuePropName="checked"
                    >
                        <Switch />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};
